import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useNotificationStore, type NotificationEntry } from "@/stores";
import {
  notificationSeverityTokens,
  type NotificationSeverityName,
} from "@/lib/tokens";
import NotificationPanel from "@/components/notifications/NotificationPanel";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { useLocation } from "wouter";

interface NotificationButtonProps {
  buttonClassName?: string;
  iconClassName?: string;
  ariaLabel?: string;
}

const SEVERITY_PRIORITY: NotificationSeverityName[] = [
  "critical",
  "error",
  "warning",
  "success",
  "info",
];

export default function NotificationButton({
  buttonClassName = "h-11 w-11 rounded-full",
  iconClassName = "h-5 w-5",
  ariaLabel = "Notifications",
}: NotificationButtonProps) {
  const [, setLocation] = useLocation();
  const notifications = useNotificationStore((state) => state.notifications);
  const unreadCount = useNotificationStore((state) => state.count);
  const severityCounts = useNotificationStore((state) => state.severityCounts);
  const totalCounts = useNotificationStore((state) => state.totalCounts);
  const loading = useNotificationStore((state) => state.loading);
  const error = useNotificationStore((state) => state.error);
  const lastSynced = useNotificationStore((state) => state.lastSynced);
  const fetchNotifications = useNotificationStore((state) => state.fetchNotifications);
  const markAsRead = useNotificationStore((state) => state.markAsRead);
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead);
  const stopLiveUpdates = useNotificationStore((state) => state.stopLiveUpdates);

  const [isOpen, setIsOpen] = useState(false);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const fetchRef = useRef(fetchNotifications);
  const stopLiveUpdatesRef = useRef(stopLiveUpdates);

  useEffect(() => {
    fetchRef.current = fetchNotifications;
  }, [fetchNotifications]);

  useEffect(() => {
    stopLiveUpdatesRef.current = stopLiveUpdates;
  }, [stopLiveUpdates]);

  useEffect(() => {
    void fetchRef.current();
    return () => {
      stopLiveUpdatesRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShouldAutoFocus(true);
      void fetchRef.current();
    } else {
      setShouldAutoFocus(false);
    }
  }, [isOpen]);

  const accentSeverity = useMemo<NotificationSeverityName>(() => {
    const unreadSeverity =
      SEVERITY_PRIORITY.find((severity) => (severityCounts[severity] ?? 0) > 0) ??
      null;
    if (unreadSeverity) return unreadSeverity;
    return (
      SEVERITY_PRIORITY.find((severity) => (totalCounts[severity] ?? 0) > 0) ??
      "info"
    );
  }, [severityCounts, totalCounts]);

  const accentTokens = notificationSeverityTokens[accentSeverity];
  const badgeVisible = unreadCount > 0;
  const badgeLabel = unreadCount > 99 ? "99+" : unreadCount.toString();

  const handleRefresh = () => {
    void fetchRef.current();
  };

  const handleNavigate = (notification: NotificationEntry) => {
    if (!notification.actionUrl) return;
    if (typeof window !== "undefined") {
      window.open(notification.actionUrl, "_blank", "noopener,noreferrer");
    }
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2",
            !prefersReducedMotion && "transition-colors duration-150",
            buttonClassName,
          )}
          style={
            badgeVisible
              ? {
                  boxShadow: `inset 0 0 0 1px ${accentTokens.border}`,
                  backgroundColor: "rgba(255,255,255,0.95)",
                }
              : undefined
          }
          data-testid="button-notifications"
          aria-label={
            badgeVisible
              ? `${unreadCount} unread notifications`
              : ariaLabel
          }
        >
          <Bell
            className={cn(
              iconClassName,
              badgeVisible ? "text-slate-900" : "text-slate-500",
            )}
            style={badgeVisible ? { color: accentTokens.accent } : undefined}
            aria-hidden="true"
          />
          {badgeVisible ? (
            <span
              className="absolute -top-1.5 -right-1.5 inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-xs font-semibold text-white"
              style={{ backgroundColor: accentTokens.accent }}
              data-testid="text-notification-count"
            >
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={12}
        className={cn(
          "w-[28rem] max-w-[90vw] border border-slate-200 p-0 shadow-xl sm:w-[30rem]",
          prefersReducedMotion &&
            "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none motion-reduce:transition-none",
        )}
      >
        <NotificationPanel
          notifications={notifications}
          loading={loading}
          error={error}
          unreadCount={unreadCount}
          severityCounts={severityCounts}
          totalCounts={totalCounts}
          onRefresh={handleRefresh}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onClose={() => setIsOpen(false)}
          onNavigate={handleNavigate}
          lastSynced={lastSynced}
          reducedMotion={prefersReducedMotion}
          autoFocus={shouldAutoFocus}
          onOpenCenter={() => {
            setIsOpen(false);
            setLocation("/notifications");
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
